import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import NamedLink from '../../../components/NamedLink/NamedLink';
import './BannerAnimated.css';

// Import optimized WebP images from assets folder
import car1Image from '../../../assets/optimized/img-1.webp';
import car2Image from '../../../assets/optimized/img-2.webp';
import car3Image from '../../../assets/optimized/img-3.webp';
import car4Image from '../../../assets/optimized/img-4.webp';

// Minimum time to show static hero before starting animation (prevents flash)
const MIN_STATIC_DISPLAY_MS = 1500;

// Preload images and return a promise that resolves when all are loaded
const preloadImages = (imageSources) => {
  return Promise.all(
    imageSources.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false); // Don't block on errors
        img.src = src;
      });
    })
  );
};

// Check if user prefers reduced motion
const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const BannerAnimated = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Skip animation setup if user prefers reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    // Images to preload (car 2, 3, 4 - car 1 is already eager loaded)
    const imagesToPreload = [car2Image, car3Image, car4Image];
    const startTime = Date.now();

    preloadImages(imagesToPreload).then(() => {
      // Calculate remaining time to meet minimum display duration
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_STATIC_DISPLAY_MS - elapsed);

      // Start animation after minimum display time
      setTimeout(() => {
        setIsReady(true);
      }, remainingTime);
    });
  }, []);

  const heroClassName = `hero${isReady ? ' hero--ready' : ''}`;

  return (
    <>
      {/* Preload hero images for better LCP */}
      <Helmet>
        <link rel="preload" as="image" href={car1Image} fetchpriority="high" />
      </Helmet>
      <div className={heroClassName}>
        {/* SEO H1 Fallback - Visually hidden but readable by search engines */}
        <h1 className="visuallyHidden">
          Buy and sell Porsche cars, parts and memorabilia on a global Porsche-only marketplace.
          List Porsche items quickly and connect with enthusiasts worldwide.
        </h1>

        {/* Rotating Hero Text */}
        <p className="text" id="text">
          Buy. Sell. Discover. <br />
          Everything Porsche.
        </p>
        <p className="text" id="text-2">
          List Your Porsche Items <br />
          In Minutes — It's That Simple.
        </p>
        <p className="text" id="text-3">
          Cars. Parts. Memorabilia. <br />
          New &amp; Used. Worldwide.
        </p>
        <p className="text" id="text-4">
          A 100% Porsche Marketplace <br />
          Built for Enthusiasts.
        </p>

        {/* CTA Buttons */}
        <div className="cta">
          <NamedLink
            name="EditListingPage"
            params={{
              id: '00000000-0000-0000-0000-000000000000',
              slug: 'draft',
              type: 'new',
              tab: 'details',
            }}
            className="ctaLink sell"
          >
            <span className="labelDesktop">List Porsche for Sale</span>
            <span className="labelMobile">SELL</span>
          </NamedLink>
          <NamedLink name="CategoriesPage" className="ctaLink buy">
            <span className="labelDesktop">View Porsche Listings</span>
            <span className="labelMobile">BUY</span>
          </NamedLink>
        </div>

        {/* Rotating Car Images */}
        <img src={car1Image} className="car-img" id="car-1-img" alt="Porsche car listings" loading="eager" fetchpriority="high" />
        <img src={car2Image} className="car-img" id="car-2-img" alt="Porsche parts and accessories" loading="lazy" />
        <img src={car3Image} className="car-img" id="car-3-img" alt="Classic and modern Porsche vehicles" loading="lazy" />
        <img src={car4Image} className="car-img" id="car-4-img" alt="Porsche memorabilia and collectibles" loading="lazy" />
      </div>
    </>
  );
};

export default BannerAnimated;
